import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faCheck, faSearch, faChevronDown, faChevronUp, faSliders
} from '@fortawesome/free-solid-svg-icons';
import { useCurrency } from '../context/CurrencyContext';
import {
  TAXONOMY_TREE,
  PRIMARY_CATEGORIES,
  isPrimaryFullySelected,
  isPrimaryPartiallySelected,
  togglePrimaryCategory,
} from '../utils/taxonomy';
import '../StylesComponents/filter-sort-panel.css';

const SECTION_IDS = ['category', 'gender', 'price', 'size', 'colour', 'discount', 'brand', 'retailer'];

function FilterSortPanel({
  isOpen = false,
  onClose = () => {},
  isFilterView = true,
  handleFilterChange = () => {},
  handleSortChange = () => {},
  handleBrandChange = () => {},
  handleRetailerChange = () => {},
  handleGenderChange = () => {},
  handleMaxPriceChange = () => {},
  selectedFilters = [],
  selectedSort = 'Default',
  selectedBrands = [],
  selectedRetailers = [],
  selectedGenders = [],
  selectedMaxPrice = 1000,
  categories = [],
  sortOptions = [],
  brands = [],
  retailers = [],
  filterConfig = { priceRanges: [], discountRanges: [], sizes: [], colors: [] },
  activeFilters = [],
  handlePriceRangeChange = () => {},
  handleDiscountRangeChange = () => {},
  handleSizeChange = () => {},
  handleColorChange = () => {},
  clearAllFilters = () => {},
  selectedPriceRanges = [],
  selectedDiscountRanges = [],
  selectedSizes = [],
  selectedColors = [],
  multiOfferOnly = false,
  handleMultiOfferChange = () => {},
  // Optional: restrict which primary categories are shown (e.g. ['Tops','Bottoms'] for MensPage)
  visiblePrimaries = null,
}) {
  const { getCurrencySymbol } = useCurrency();

  const [brandSearch, setBrandSearch] = useState('');
  const [retailerSearch, setRetailerSearch] = useState('');

  // Which accordion sections are expanded
  const [expandedSections, setExpandedSections] = useState(
    () => new Set(['category', 'gender', 'price'])
  );

  // Which primary categories are expanded to show subcategories
  const [expandedPrimaries, setExpandedPrimaries] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) {
      setBrandSearch('');
      setRetailerSearch('');
    }
  }, [isOpen]);

  // Auto-expand primaries that have selections
  useEffect(() => {
    if (selectedFilters.length > 0) {
      const toExpand = new Set(expandedPrimaries);
      PRIMARY_CATEGORIES.forEach(pk => {
        const subs = TAXONOMY_TREE[pk]?.subcategories.map(s => s.key) || [];
        if (subs.some(k => selectedFilters.includes(k))) {
          toExpand.add(pk);
        }
      });
      setExpandedPrimaries(toExpand);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const togglePrimary = useCallback((pk) => {
    setExpandedPrimaries(prev => {
      const next = new Set(prev);
      if (next.has(pk)) next.delete(pk);
      else next.add(pk);
      return next;
    });
  }, []);

  const handlePrimaryCheckbox = useCallback((pk) => {
    const newSelected = togglePrimaryCategory(pk, selectedFilters);
    // Sync back via handleFilterChange for each changed subcategory
    const subs = TAXONOMY_TREE[pk]?.subcategories.map(s => s.key) || [];
    const fullyWas = subs.every(k => selectedFilters.includes(k));
    subs.forEach(sub => {
      const isNowSelected = newSelected.includes(sub);
      const wasSelected = selectedFilters.includes(sub);
      if (isNowSelected !== wasSelected) {
        handleFilterChange(sub);
      }
    });
  }, [selectedFilters, handleFilterChange]);

  const activeFilterCount = activeFilters.length;

  const filteredBrands = brands.filter(brand => {
    const name = (brand.username || brand.name || (typeof brand === 'string' ? brand : '')).toLowerCase();
    return name.includes(brandSearch.toLowerCase());
  });

  const filteredRetailers = retailers.filter(retailer => {
    const name = (typeof retailer === 'string' ? retailer : (retailer.name || '')).toLowerCase();
    return name.includes(retailerSearch.toLowerCase());
  });

  const primariesToShow = visiblePrimaries
    ? PRIMARY_CATEGORIES.filter(pk => visiblePrimaries.includes(pk))
    : PRIMARY_CATEGORIES;

  // ── Section header helper ──────────────────────────────────────────────────
  const SectionHeader = ({ id, label, count }) => (
    <button
      className="fsp-section-header"
      onClick={() => toggleSection(id)}
      aria-expanded={expandedSections.has(id)}
    >
      <span className="fsp-section-label">
        {label}
        {count > 0 && <span className="fsp-section-badge">{count}</span>}
      </span>
      <FontAwesomeIcon
        icon={expandedSections.has(id) ? faChevronUp : faChevronDown}
        className="fsp-section-chevron"
      />
    </button>
  );

  return (
    <>
      <div
        className={`fsp-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`fsp-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-label="Filter and Sort"
        aria-modal="true"
      >
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="fsp-header">
          <div className="fsp-header-left">
            <FontAwesomeIcon icon={faSliders} className="fsp-header-icon" />
            <span className="fsp-header-title">
              {isFilterView ? 'Filter' : 'Sort'}
            </span>
            {activeFilterCount > 0 && (
              <span className="fsp-header-count">{activeFilterCount}</span>
            )}
          </div>
          <button className="fsp-close" onClick={onClose} aria-label="Close panel">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Active filter chips ───────────────────────────────────────────── */}
        {activeFilters.length > 0 && (
          <div className="fsp-active-bar">
            <div className="fsp-chips">
              {activeFilters.map(filter => (
                <button
                  key={filter.id}
                  className="fsp-chip"
                  onClick={() => filter.onRemove(filter.id)}
                >
                  {filter.label}
                  <FontAwesomeIcon icon={faTimes} className="fsp-chip-remove" />
                </button>
              ))}
            </div>
            <button className="fsp-clear-all" onClick={clearAllFilters}>
              Clear all
            </button>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────────────────────── */}
        <div className="fsp-body">
          {isFilterView ? (
            <>
              {/* ── CATEGORY ─────────────────────────────────────────────── */}
              <div className="fsp-section">
                <SectionHeader
                  id="category"
                  label="Category"
                  count={selectedFilters.length}
                />
                {expandedSections.has('category') && (
                  <div className="fsp-section-content">
                    {/* Market Alerts toggle */}
                    <label className="fsp-row fsp-row--highlight">
                      <input
                        type="checkbox"
                        checked={multiOfferOnly}
                        onChange={e => handleMultiOfferChange(e.target.checked)}
                        className="fsp-hidden-input"
                      />
                      <span className={`fsp-checkbox ${multiOfferOnly ? 'checked' : ''}`}>
                        {multiOfferOnly && <FontAwesomeIcon icon={faCheck} />}
                      </span>
                      <span className="fsp-row-label fsp-row-label--accent">
                        Comparison Deals Only
                      </span>
                    </label>

                    {/* Hierarchical taxonomy categories */}
                    {primariesToShow.map(pk => {
                      const node = TAXONOMY_TREE[pk];
                      if (!node) return null;
                      const fullySelected = isPrimaryFullySelected(pk, selectedFilters);
                      const partiallySelected = isPrimaryPartiallySelected(pk, selectedFilters);
                      const isExpanded = expandedPrimaries.has(pk);

                      return (
                        <div key={pk} className="fsp-primary-group">
                          <div className="fsp-primary-row">
                            <label
                              className="fsp-row fsp-row--primary"
                              onClick={e => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={fullySelected}
                                onChange={() => handlePrimaryCheckbox(pk)}
                                className="fsp-hidden-input"
                              />
                              <span className={`fsp-checkbox ${fullySelected ? 'checked' : partiallySelected ? 'partial' : ''}`}>
                                {fullySelected && <FontAwesomeIcon icon={faCheck} />}
                                {partiallySelected && !fullySelected && (
                                  <span className="fsp-partial-dot" />
                                )}
                              </span>
                              <span className="fsp-row-label fsp-row-label--primary">
                                {node.label}
                              </span>
                            </label>
                            <button
                              className="fsp-expand-btn"
                              onClick={() => togglePrimary(pk)}
                              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.label}`}
                            >
                              <FontAwesomeIcon
                                icon={isExpanded ? faChevronUp : faChevronDown}
                                className="fsp-expand-icon"
                              />
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="fsp-subcategories">
                              {node.subcategories.map(sub => (
                                <label key={sub.key} className="fsp-row fsp-row--sub">
                                  <input
                                    type="checkbox"
                                    checked={selectedFilters.includes(sub.key)}
                                    onChange={() => handleFilterChange(sub.key)}
                                    className="fsp-hidden-input"
                                  />
                                  <span className={`fsp-checkbox fsp-checkbox--sm ${selectedFilters.includes(sub.key) ? 'checked' : ''}`}>
                                    {selectedFilters.includes(sub.key) && (
                                      <FontAwesomeIcon icon={faCheck} />
                                    )}
                                  </span>
                                  <span className="fsp-row-label">{sub.label}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── GENDER ───────────────────────────────────────────────── */}
              <div className="fsp-section">
                <SectionHeader
                  id="gender"
                  label="Gender"
                  count={selectedGenders.length}
                />
                {expandedSections.has('gender') && (
                  <div className="fsp-section-content">
                    <div className="fsp-gender-grid">
                      {['Men', 'Women', 'Kids', 'Unisex'].map(g => {
                        const val = g.toLowerCase();
                        const active = selectedGenders.includes(val);
                        return (
                          <button
                            key={g}
                            className={`fsp-gender-btn ${active ? 'active' : ''}`}
                            onClick={() => handleGenderChange(val)}
                          >
                            {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ── PRICE ────────────────────────────────────────────────── */}
              <div className="fsp-section">
                <SectionHeader
                  id="price"
                  label="Price"
                  count={selectedPriceRanges.length + (selectedMaxPrice < 1000 ? 1 : 0)}
                />
                {expandedSections.has('price') && (
                  <div className="fsp-section-content">
                    <div className="fsp-price-display">
                      <span>{getCurrencySymbol()}0</span>
                      <span className="fsp-price-value">
                        {getCurrencySymbol()}{selectedMaxPrice}{selectedMaxPrice === 1000 ? '+' : ''}
                      </span>
                    </div>
                    <div className="fsp-slider-wrap">
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        step="25"
                        value={selectedMaxPrice}
                        onChange={e => handleMaxPriceChange(parseInt(e.target.value))}
                        className="fsp-slider"
                      />
                    </div>

                    {filterConfig.priceRanges?.length > 0 && (
                      <div className="fsp-option-list">
                        {filterConfig.priceRanges.map(range => (
                          <label key={range.id} className="fsp-row">
                            <input
                              type="checkbox"
                              checked={selectedPriceRanges.includes(range.id)}
                              onChange={() => handlePriceRangeChange(range.id)}
                              className="fsp-hidden-input"
                            />
                            <span className={`fsp-checkbox ${selectedPriceRanges.includes(range.id) ? 'checked' : ''}`}>
                              {selectedPriceRanges.includes(range.id) && <FontAwesomeIcon icon={faCheck} />}
                            </span>
                            <span className="fsp-row-label">{range.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── SIZE ─────────────────────────────────────────────────── */}
              {filterConfig.sizes?.length > 0 && (
                <div className="fsp-section">
                  <SectionHeader
                    id="size"
                    label="Size"
                    count={selectedSizes.length}
                  />
                  {expandedSections.has('size') && (
                    <div className="fsp-section-content">
                      {(Array.isArray(filterConfig.sizes[0]) || typeof filterConfig.sizes[0] === 'object')
                        ? filterConfig.sizes.map((section, i) => (
                          <div key={section.label || i} className="fsp-size-group">
                            <span className="fsp-size-group-label">{section.label}</span>
                            <div className="fsp-size-grid">
                              {section.values.map(size => (
                                <button
                                  key={size}
                                  className={`fsp-size-tile ${selectedSizes.includes(size) ? 'selected' : ''}`}
                                  onClick={() => handleSizeChange(size)}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))
                        : (
                          <div className="fsp-size-grid">
                            {filterConfig.sizes.map(size => (
                              <button
                                key={size}
                                className={`fsp-size-tile ${selectedSizes.includes(size) ? 'selected' : ''}`}
                                onClick={() => handleSizeChange(size)}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        )
                      }
                    </div>
                  )}
                </div>
              )}

              {/* ── COLOUR ───────────────────────────────────────────────── */}
              {filterConfig.colors?.length > 0 && (
                <div className="fsp-section">
                  <SectionHeader
                    id="colour"
                    label="Colour"
                    count={selectedColors.length}
                  />
                  {expandedSections.has('colour') && (
                    <div className="fsp-section-content">
                      <div className="fsp-color-grid">
                        {filterConfig.colors.map(color => (
                          <button
                            key={color.id}
                            className={`fsp-color-swatch ${selectedColors.includes(color.id) ? 'selected' : ''}`}
                            style={{ backgroundColor: color.hex, borderColor: color.hex === '#FFFFFF' ? '#ddd' : color.hex }}
                            onClick={() => handleColorChange(color.id)}
                            title={color.label}
                            aria-label={color.label}
                          >
                            {selectedColors.includes(color.id) && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className={color.hex === '#FFFFFF' ? 'dark' : ''}
                              />
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="fsp-color-labels">
                        {filterConfig.colors.map(color => (
                          selectedColors.includes(color.id) && (
                            <span key={color.id} className="fsp-color-label-text">{color.label}</span>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── DISCOUNT ─────────────────────────────────────────────── */}
              {filterConfig.discountRanges?.length > 0 && (
                <div className="fsp-section">
                  <SectionHeader
                    id="discount"
                    label="Discount"
                    count={selectedDiscountRanges.length}
                  />
                  {expandedSections.has('discount') && (
                    <div className="fsp-section-content">
                      {filterConfig.discountRanges.map(range => (
                        <label key={range.id} className="fsp-row">
                          <input
                            type="checkbox"
                            checked={selectedDiscountRanges.includes(range.id)}
                            onChange={() => handleDiscountRangeChange(range.id)}
                            className="fsp-hidden-input"
                          />
                          <span className={`fsp-checkbox ${selectedDiscountRanges.includes(range.id) ? 'checked' : ''}`}>
                            {selectedDiscountRanges.includes(range.id) && <FontAwesomeIcon icon={faCheck} />}
                          </span>
                          <span className="fsp-row-label">{range.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── BRAND ────────────────────────────────────────────────── */}
              {brands.length > 0 && (
                <div className="fsp-section">
                  <SectionHeader
                    id="brand"
                    label="Brand"
                    count={selectedBrands.length}
                  />
                  {expandedSections.has('brand') && (
                    <div className="fsp-section-content">
                      <div className="fsp-search-box">
                        <FontAwesomeIcon icon={faSearch} className="fsp-search-icon" />
                        <input
                          type="text"
                          placeholder="Search brands..."
                          value={brandSearch}
                          onChange={e => setBrandSearch(e.target.value)}
                          className="fsp-search-input"
                        />
                        {brandSearch && (
                          <button className="fsp-search-clear" onClick={() => setBrandSearch('')}>
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      <div className="fsp-scrollable-list">
                        {filteredBrands.map((brand, idx) => {
                          const name = brand.username || brand.name || (typeof brand === 'string' ? brand : '');
                          if (!name) return null;
                          return (
                            <label key={brand._id || brand.id || `brand-${idx}`} className="fsp-row">
                              <input
                                type="checkbox"
                                checked={selectedBrands.includes(name)}
                                onChange={() => handleBrandChange(name)}
                                className="fsp-hidden-input"
                              />
                              <span className={`fsp-checkbox ${selectedBrands.includes(name) ? 'checked' : ''}`}>
                                {selectedBrands.includes(name) && <FontAwesomeIcon icon={faCheck} />}
                              </span>
                              <span className="fsp-row-label">{name}</span>
                            </label>
                          );
                        })}
                        {filteredBrands.length === 0 && (
                          <p className="fsp-empty">No brands found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── RETAILER ─────────────────────────────────────────────── */}
              {retailers.length > 0 && (
                <div className="fsp-section">
                  <SectionHeader
                    id="retailer"
                    label="Retailer"
                    count={selectedRetailers.length}
                  />
                  {expandedSections.has('retailer') && (
                    <div className="fsp-section-content">
                      <div className="fsp-search-box">
                        <FontAwesomeIcon icon={faSearch} className="fsp-search-icon" />
                        <input
                          type="text"
                          placeholder="Search retailers..."
                          value={retailerSearch}
                          onChange={e => setRetailerSearch(e.target.value)}
                          className="fsp-search-input"
                        />
                        {retailerSearch && (
                          <button className="fsp-search-clear" onClick={() => setRetailerSearch('')}>
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      <div className="fsp-scrollable-list">
                        {filteredRetailers.map((retailer, idx) => {
                          const name = typeof retailer === 'string' ? retailer : (retailer.name || '');
                          if (!name) return null;
                          return (
                            <label key={`retailer-${idx}`} className="fsp-row">
                              <input
                                type="checkbox"
                                checked={selectedRetailers.includes(name)}
                                onChange={() => handleRetailerChange(name)}
                                className="fsp-hidden-input"
                              />
                              <span className={`fsp-checkbox ${selectedRetailers.includes(name) ? 'checked' : ''}`}>
                                {selectedRetailers.includes(name) && <FontAwesomeIcon icon={faCheck} />}
                              </span>
                              <span className="fsp-row-label">{name}</span>
                            </label>
                          );
                        })}
                        {filteredRetailers.length === 0 && (
                          <p className="fsp-empty">No retailers found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* ── SORT VIEW ──────────────────────────────────────────────── */
            <div className="fsp-sort-list">
              {sortOptions.map(opt => (
                <button
                  key={opt.value}
                  className={`fsp-sort-btn ${selectedSort === opt.value ? 'selected' : ''}`}
                  onClick={() => handleSortChange(opt.value)}
                >
                  {opt.label}
                  {selectedSort === opt.value && (
                    <span className="fsp-sort-indicator" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Sticky footer ────────────────────────────────────────────────── */}
        <div className="fsp-footer">
          <button className="fsp-btn fsp-btn--reset" onClick={clearAllFilters}>
            Reset
          </button>
          <button className="fsp-btn fsp-btn--apply" onClick={onClose}>
            {activeFilterCount > 0 ? `View results (${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''})` : 'View results'}
          </button>
        </div>
      </div>
    </>
  );
}

export default FilterSortPanel;
